import {
  Checkout,
  CheckoutItem,
} from "../../../../common/api/decimal/methods/compareAndPurchase.ts";
import { FormattedMessage, useIntl } from "react-intl";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import EmptyAssetCard from "../../../../common/features/asset/EmptyAssetCard.tsx";
import styles from "./RetailerSegment.module.scss";
import { clsx } from "clsx";
import MaterialTags from "../common/MaterialTags.tsx";
import ItemCondition from "../common/ItemCondition.tsx";
import { Form } from "react-router-dom";

function PurchaseItemButton({
  item,
  hasSharedUrl,
}: {
  item: CheckoutItem;
  hasSharedUrl: boolean;
}) {
  if (!hasSharedUrl) {
    return (
      <BaseLinkOrButton
        href={item.checkoutUrl}
        className={clsx("vst-button-pill", styles.purchaseItem)}
      >
        <FormattedMessage defaultMessage="Purchase From Retailer" />
      </BaseLinkOrButton>
    );
  }
}

export default function RetailerSegment({
  checkout,
  className,
}: {
  checkout: Checkout;
  className?: string;
}) {
  const intl = useIntl();
  const cost = intl.formatNumber(Number(checkout.totalPrice) ?? 0, {
    style: "currency",
    currency: "USD",
  });

  const purchaseOptionURL = ({ catalogId, courseId, isbn }: CheckoutItem) =>
    `/my-courses/purchase-options/${catalogId}/${courseId}/${isbn}`;

  return (
    <section className={clsx(styles.container, className)}>
      <div className={styles.headerArea}>
        <div className={styles.retailerPlusCount}>
          <h2 className={styles.retailerName}>{checkout.retailerName}</h2>
          <span className={styles.itemCount}>
            <FormattedMessage
              defaultMessage="{count, plural, =1 {1 item} other {# items}}"
              values={{ count: checkout.items.length }}
            />
          </span>
        </div>
        {checkout.hasSharedUrl && (
          <BaseLinkOrButton
            href={checkout.checkoutUrl}
            className="vst-button-pill"
          >
            <FormattedMessage defaultMessage="Purchase From Retailer" />
          </BaseLinkOrButton>
        )}
        <span className={styles.subtotal}>
          <FormattedMessage
            defaultMessage="Subtotal: {cost}"
            values={{ cost }}
          />
        </span>
      </div>
      <ul>
        {checkout.items.map((item) => (
          <li key={item.itemId} className={styles.item}>
            <EmptyAssetCard
              vbid={item.isbn}
              type="horizontal"
              tagName="div"
              contentAreaClassName={styles.assetCardContent}
            >
              <div className={styles.textLines}>
                <div className={styles.typeAndCondition}>
                  <MaterialTags format={item.variant} />
                  <ItemCondition item={item} className={styles.details} />
                </div>
                <h3 className={styles.itemName}>{item.title}</h3>
                {item.author && (
                  <div className={styles.metadata}>{item.author}</div>
                )}
                <div className={styles.metadata}>
                  <FormattedMessage
                    defaultMessage="ISBN: {isbn}"
                    values={{ isbn: item.isbn }}
                  />
                </div>
              </div>
              <div className={styles.rightContent}>
                <span className={styles.itemPrice}>{item.formattedPrice}</span>
                <PurchaseItemButton
                  item={item}
                  hasSharedUrl={checkout.hasSharedUrl}
                />

                <div className={styles.links}>
                  <BaseLinkOrButton
                    to={purchaseOptionURL(item)}
                    aria-label={intl.formatMessage(
                      { defaultMessage: "Edit {title}" },
                      { title: item.title }
                    )}
                  >
                    <FormattedMessage defaultMessage="Edit" />
                  </BaseLinkOrButton>

                  <Form
                    className={styles.removeForm}
                    action="/my-courses/update-cart"
                    method="DELETE"
                    navigate={false}
                  >
                    <BaseLinkOrButton
                      type="submit"
                      aria-label={intl.formatMessage(
                        { defaultMessage: "Remove {title}" },
                        { title: item.title }
                      )}
                    >
                      <FormattedMessage defaultMessage="Remove" />
                    </BaseLinkOrButton>
                    <input type="hidden" name="id" value={item.primaryKeyId} />
                  </Form>
                </div>
              </div>
            </EmptyAssetCard>
            <PurchaseItemButton
              item={item}
              hasSharedUrl={checkout.hasSharedUrl}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
